import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Box, Paper, Typography, Button, FormControl, InputLabel, Select, MenuItem, Grid } from "@mui/material";
import { styled } from "@mui/material/styles";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { Table } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { incentivesApi } from "../../Components/incentivesApi";
import { useTheme } from "../../hooks/useTheme";
import { createMuiThemeFromMode } from "../../theme/theme";
import styles from "../AllEnquiries.module.scss";
import dashboardStyles from "../dashboard/Dashboard.module.scss";

const PAGE_SIZE = 20;

const baseThemeOptions = {
  typography: { fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", button: { textTransform: "none" } },
  shape: { borderRadius: 12 },
};

const FilterPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "#ffffff",
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
}));

function thisMonthRange() {
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  return {
    from: first.toISOString().slice(0, 10),
    to: today.toISOString().slice(0, 10),
  };
}

export default function IncentivesCommissionDetail() {
  const { themeMode } = useTheme();
  const theme = useMemo(
    () => createTheme(createMuiThemeFromMode(themeMode, baseThemeOptions)),
    [themeMode]
  );
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultRange = thisMonthRange();
  const dateFrom = searchParams.get("date_from") || defaultRange.from;
  const dateTo = searchParams.get("date_to") || defaultRange.to;
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState("calculated_at");
  const [direction, setDirection] = useState("desc");
  const [statusFilter, setStatusFilter] = useState("");
  const abortRef = useRef(null);

  const fetchList = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset, order, direction, date_from: dateFrom, date_to: dateTo };
      if (statusFilter) params.status = statusFilter;
      const res = await incentivesApi.getCommissionList(params, abortRef.current.signal);
      setResults(res.results || []);
      setTotal(res.total ?? 0);
    } catch (e) {
      if (e.name !== "AbortError") setResults([]);
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  }, [offset, order, direction, dateFrom, dateTo, statusFilter]);

  useEffect(() => {
    fetchList();
    return () => abortRef.current?.abort();
  }, [fetchList]);

  const totalPages = Math.ceil(total / PAGE_SIZE) || 1;
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const orderValue = `${order}_${direction}`;

  return (
    <ThemeProvider theme={theme}>
      <Box className={`${styles.root} ${dashboardStyles.dashboardContent}`}>
        <Paper elevation={0} className={`${styles.contentPaper} ${dashboardStyles.contentPaper}`}>
          <Box sx={{ marginBottom: 2 }}>
            <Box className={styles.header}>
              <Box>
                <Typography variant="h4" className={styles.pageTitle}>
                  Commission details
                </Typography>
                <Typography variant="body2" className={styles.pageSubtitle} sx={{ mt: 0.5 }}>
                  Period: {dateFrom} to {dateTo}
                </Typography>
              </Box>
              <Box className={styles.headerActions}>
                <Button variant="outlined" onClick={() => navigate("/incentives")}>
                  Back to dashboard
                </Button>
              </Box>
            </Box>
          </Box>

          <FilterPaper variant="outlined">
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sort</InputLabel>
                  <Select
                    value={orderValue}
                    label="Sort"
                    onChange={(e) => {
                      const [o, d] = e.target.value.split("_");
                      setOrder(o);
                      setDirection(d);
                      setOffset(0);
                    }}
                  >
                    <MenuItem value="calculated_at_desc">Newest first</MenuItem>
                    <MenuItem value="calculated_at_asc">Oldest first</MenuItem>
                    <MenuItem value="commission_amount_desc">Amount (high first)</MenuItem>
                    <MenuItem value="commission_amount_asc">Amount (low first)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setOffset(0);
                    }}
                  >
                    <MenuItem value="">All statuses</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </FilterPaper>

          {loading && <Skeleton count={10} height={40} />}
          {!loading && (
            <>
              <Table responsive bordered hover>
                <thead>
                  <tr>
                    <th>Structure</th>
                    <th>Period</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Calculated at</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => (
                    <tr key={r.id}>
                      <td>{r.structure_name || "—"}</td>
                      <td>{r.period_start && r.period_end ? `${r.period_start} – ${r.period_end}` : "—"}</td>
                      <td>{r.commission_amount != null ? Number(r.commission_amount).toLocaleString(undefined, { minimumFractionDigits: 2 }) : "—"}</td>
                      <td>{r.status}</td>
                      <td>{r.calculated_at ? new Date(r.calculated_at).toLocaleString() : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {results.length === 0 && (
                <Typography color="text.secondary" variant="body2" sx={{ py: 2 }}>
                  No commission records in this period.
                </Typography>
              )}
              {total > 0 && (
                <Box className={styles.pagination} sx={{ flexWrap: "wrap", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Button size="small" variant="outlined" disabled={offset === 0} onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}>
                      Previous
                    </Button>
                    <Typography variant="body2" component="span">
                      Page {currentPage} of {totalPages}
                    </Typography>
                    <Button size="small" variant="outlined" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset((o) => o + PAGE_SIZE)}>
                      Next
                    </Button>
                  </Box>
                </Box>
              )}
            </>
          )}
        </Paper>
      </Box>
    </ThemeProvider>
  );
}
